<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { gsap } from 'gsap'

const props = defineProps<{
  id: number
  x: number
  y: number
  type?: 'normal' | 'antigravity' | 'superconductor' | 'blackhole'
}>()

const emit = defineEmits<{
  destroyed: [id: number]
}>()

// 随机生成量子饲料类型
const type = ref(
  props.type ||
    (['normal', 'antigravity', 'superconductor', 'blackhole'] as const)[
      Math.random() > 0.8 ? Math.floor(Math.random() * 4) : 0
    ],
)

// 薛定谔状态
const isSchrodinger = ref(Math.random() > 0.85)
const schrodingerState = ref<'visible' | 'invisible'>('visible')

// 位置和动画参数
const position = ref({
  x: props.x,
  y: props.y,
})
const rotation = ref(Math.random() * 360)
const scale = ref(0)
const opacity = ref(1)

// 生成随机几何形状
const shapeType = ref(Math.floor(Math.random() * 5))
const shapeStyles = [
  { clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }, // 五边形
  { clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }, // 六边形
  {
    clipPath:
      'polygon(0% 15%, 15% 15%, 15% 0%, 85% 0%, 85% 15%, 100% 15%, 100% 85%, 85% 85%, 85% 100%, 15% 100%, 15% 85%, 0% 85%)',
  }, // 加号
  { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' }, // 方形
  { clipPath: 'circle(50% at 50% 50%)' }, // 圆形
]

// 随机颜色
const hue = ref(Math.random() * 360)
const saturation = ref(70 + Math.random() * 30)
const lightness = ref(50 + Math.random() * 20)

// 饲料样式
const foodStyle = computed(() => ({
  transform: `translate(${position.value.x}px, ${position.value.y}px) rotate(${rotation.value}deg) scale(${scale.value})`,
  background: `hsl(${hue.value}, ${saturation.value}%, ${lightness.value}%)`,
  opacity: opacity.value,
}))

// 根据类型获取样式
const foodTypeStyle = computed(() => {
  switch (type.value) {
    case 'antigravity':
      return {
        boxShadow: `0 0 15px hsl(${hue.value}, ${saturation.value}%, ${lightness.value + 20}%)`,
        border: '1px solid white',
        animation: 'float 2s infinite alternate',
      }
    case 'superconductor':
      return {
        boxShadow: `0 0 25px hsl(${hue.value}, ${saturation.value}%, ${lightness.value + 30}%)`,
        backdropFilter: 'blur(5px)',
        mixBlendMode: 'exclusion' as const,
      }
    case 'blackhole':
      return {
        boxShadow: `0 0 30px black`,
        background: `radial-gradient(circle, rgba(0,0,0,1) 30%, hsl(${hue.value}, ${saturation.value}%, ${lightness.value - 20}%) 100%)`,
        filter: 'contrast(1.5)',
      }
    default:
      return {
        boxShadow: `0 0 10px hsl(${hue.value}, ${saturation.value}%, ${lightness.value}%)`,
      }
  }
})

// 饲料出场动画
onMounted(() => {
  // 出现动画
  gsap.to(scale, {
    value: 1,
    duration: 0.4,
    ease: 'back.out(2)',
    onComplete: () => {
      // 薛定谔的投喂状态转换
      if (isSchrodinger.value) {
        const intervalId = setInterval(
          () => {
            schrodingerState.value = schrodingerState.value === 'visible' ? 'invisible' : 'visible'
            opacity.value = schrodingerState.value === 'visible' ? 1 : 0.3
          },
          300 + Math.random() * 500,
        )

        // 确保组件卸载时清除定时器
        onBeforeUnmount(() => {
          clearInterval(intervalId)
        })
      }

      // 应用特定类型的饲料行为
      applyTypeSpecificBehavior()
    },
  })

  // 旋转动画
  gsap.to(rotation, {
    value: rotation.value + 360,
    duration: 10,
    ease: 'none',
    repeat: -1,
  })
})

// 根据饲料类型应用特殊行为
const applyTypeSpecificBehavior = () => {
  switch (type.value) {
    case 'antigravity':
      // 反重力效果
      gsap.to(position, {
        y: position.value.y - 100 - Math.random() * 200,
        duration: 10,
        ease: 'power1.inOut',
      })
      break

    case 'superconductor':
      // 超导效果 - 快速移动
      const randomMove = () => {
        gsap.to(position, {
          x: position.value.x + (Math.random() - 0.5) * 80,
          y: position.value.y + (Math.random() - 0.5) * 80,
          duration: 0.5,
          ease: 'power1.inOut',
          onComplete: () => {
            if (Math.random() > 0.2) {
              randomMove()
            }
          },
        })
      }
      randomMove()
      break

    case 'blackhole':
      // 黑洞效果 - 脉冲扩散
      gsap.to(scale, {
        value: 1.2,
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
      })
      break

    default:
      // 正常饲料 - 缓慢下沉
      gsap.to(position, {
        y: position.value.y + 100,
        duration: 8,
        ease: 'power1.in',
      })
  }

  // 所有饲料的生命周期
  gsap.to(opacity, {
    value: 0,
    duration: 12,
    delay: isSchrodinger.value ? 3 : 8,
    ease: 'power1.in',
    onComplete: () => {
      emit('destroyed', props.id)
    },
  })
}

// 获取饲料位置
const getPosition = () => ({
  x: position.value.x,
  y: position.value.y,
})

// 销毁饲料
const destroy = () => {
  gsap.to(scale, {
    value: 1.5,
    duration: 0.2,
    ease: 'power2.in',
    onComplete: () => {
      gsap.to(scale, {
        value: 0,
        duration: 0.3,
        ease: 'power2.out',
      })

      gsap.to(opacity, {
        value: 0,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => {
          emit('destroyed', props.id)
        },
      })
    },
  })
}

// 暴露方法给父组件
defineExpose({
  getPosition,
  destroy,
  type: type.value,
})
</script>

<template>
  <div
    class="quantum-food"
    :class="[type, { schrodinger: isSchrodinger }]"
    :style="[foodStyle, shapeStyles[shapeType], foodTypeStyle]"
  ></div>
</template>

<style scoped>
.quantum-food {
  position: absolute;
  width: 30px;
  height: 30px;
  will-change: transform, opacity;
  transition: box-shadow 0.5s ease;
  z-index: 5;
  pointer-events: none;
}

/* 薛定谔状态样式 */
.schrodinger {
  animation: flicker 0.5s ease infinite alternate;
}

/* 特殊类型样式 */
.antigravity {
  filter: brightness(1.2);
}

.superconductor {
  filter: hue-rotate(90deg);
}

.blackhole::before {
  content: '';
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.5);
  animation: pulse 1.5s infinite;
  z-index: -1;
}

@keyframes float {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-10px);
  }
}

@keyframes flicker {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.3;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}
</style>
